import { AgentObj } from "@/types/interfaces/agents";
import {
  AccountBalanceOutlined,
  BadgeOutlined,
  CampaignOutlined,
  ContactMailOutlined,
  CreditCardOutlined,
  EventNoteOutlined,
  GavelOutlined,
  HomeWorkOutlined,
  LanguageOutlined,
  MailOutlineOutlined,
  PhoneInTalkOutlined,
  PrivacyTipOutlined,
  SettingsPhoneOutlined,
  TrendingUpOutlined,
  TuneOutlined,
} from "@mui/icons-material";
import { ReactNode } from "react";
import { CERTIFICATIONS } from "../constants";
import { getBookingSystemsFullNames, getLanguagesFullNames, getWorkingDays } from "./utils";

export type ProfileFieldKind = "text" | "email" | "phone" | "url" | "secret";

export interface ProfileField {
  label: string;
  value: string | null;
  kind?: ProfileFieldKind;
  fullWidth?: boolean;
}

export interface ProfileFieldGroup {
  id: string;
  title: string;
  icon: ReactNode;
  fields: ProfileField[];
}

export type Agent = Omit<AgentObj, "_id"> | null;

// normalises API values for display; null means "not set"
export const formatFieldValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : null;

  const str = String(value).trim();
  if (!str || str === "-") return null;
  if (str === "true") return "Yes";
  if (str === "false") return "No";
  return str;
};

const formatTriState = (value: unknown): string | null => {
  if (value === 1) return "Yes";
  if (value === 0) return "No";
  if (value === 2) return "Not Available";
  return null;
};

const field = (label: string, value: unknown, kind?: ProfileFieldKind, fullWidth?: boolean): ProfileField => ({
  label,
  value: formatFieldValue(value),
  kind,
  fullWidth,
});

export const getCertificationNames = (agent: Agent): string[] =>
  (Array.isArray(agent?.certifications) ? agent?.certifications ?? [] : [])
    .map((c) => CERTIFICATIONS[c as number])
    .filter(Boolean);

// the Rising Star year in effect (same constant as the CRM clients list uses)
export const RISING_STAR_YEAR = 2026;

// current Rising Star level: y2026.rsLevel first, then the per-year map and the flat field kept in sync by admin edits
export const getRisingStarLevel = (agent: Agent): string | number | null => {
  const key = `y${RISING_STAR_YEAR}`;
  const value =
    (agent as any)?.[key]?.risingStarLevel ?? (agent as any)?.risingStarLevels?.[key] ?? agent?.risingStarLevel;
  return value === undefined || value === null || value === "" ? null : value;
};

export const getAgentAddress = (agent: Agent): string | null =>
  formatFieldValue([agent?.streetAddress, agent?.city, agent?.province, agent?.postalCode].filter(Boolean).join(", "));

export const getProfileFieldGroups = (agent: Agent): Record<string, ProfileFieldGroup> => ({
  address: {
    id: "address",
    title: "Address",
    icon: <HomeWorkOutlined />,
    fields: [field("Address", getAgentAddress(agent), "text", true)],
  },
  emails: {
    id: "emails",
    title: "Emails",
    icon: <MailOutlineOutlined />,
    fields: [
      field("TTAND Email", agent?.ttandEmail, "email"),
      field("TTAND Email Alias", agent?.ttandEmailAlias, "email"),
      field("TTAND Email Forwarded To", agent?.emailForwardedTo, "email"),
      field("Business Email", agent?.businessEmail, "email"),
      field("Personal Email", agent?.otherEmail, "email"),
      field("Backup Person Email", agent?.backupAgentEmail, "email"),
    ],
  },
  phones: {
    id: "phones",
    title: "Phones",
    icon: <PhoneInTalkOutlined />,
    fields: [
      field("Cell Phone", agent?.cellPhone, "phone"),
      field("Business Phone", agent?.businessPhone, "phone"),
      field("Home Phone", agent?.homePhone, "phone"),
      field("TTAND Extension", agent?.ttandExtension),
      field("TTAND Extension Forwarded To", agent?.extensionForwardedTo),
      field("TTAND Phone", agent?.callPlans?.ttandPhone, "phone"),
      field("Toll Free Number", agent?.callPlans?.tollfreeNumber, "phone"),
      field("Dedicated Local Number", agent?.callPlans?.dedicatedLocalNumber, "phone"),
    ],
  },
  callPlans: {
    id: "callPlans",
    title: "Call Plans & Subscriptions",
    icon: <SettingsPhoneOutlined />,
    fields: [
      field("Outgoing Call Plan", agent?.callPlans?.outgoingCallPlan),
      field("Incoming Call Plan", agent?.callPlans?.incomingCallPlan),
      field("Office 365 Subscription", agent?.office365Subscription),
    ],
  },
  account: {
    id: "account",
    title: "Account & Agreement",
    icon: <BadgeOutlined />,
    fields: [
      field("Agent Type", agent?.agentType),
      field("Agreement Type", agent?.agreement),
      field("Current Status", agent?.status),
      field("Role", agent?.role),
      field("Referral Number", agent?.referralNumber),
      field("Mentor Agent Program", agent?.mentorAgentProgram),
      field("Reporting Primary Agent", agent?.associate?.reportingPrimaryAgent, "email"),
      field("Associate Agent Commission Split", agent?.associate?.commissionSplit),
      field("Associate Agent Application Link", agent?.associateApplicationURL, "url", true),
    ],
  },
  dates: {
    id: "dates",
    title: "Key Dates",
    icon: <EventNoteOutlined />,
    fields: [
      field("Start Date in Travel Industry", agent?.dateStarted),
      field("Original TTAND Join Date", agent?.originalJoinDate),
      field("Contract Commencement Date", agent?.dates?.contractCommencement),
      field("Training Date", agent?.dates?.trainingDate),
      field("Business Card Completion Date", agent?.dates?.businessCardCompletion),
      field("Monthly Fees Payment Started", agent?.dates?.monthlyFeesChargeStarted),
      field("Annual Fees Charge Month", agent?.dates?.annualFeesChargeMonth),
      field("Birthday", agent?.dates?.birthDate),
      field("Termination Date", agent?.dates?.terminationDate),
    ],
  },
  payroll: {
    id: "payroll",
    title: "Payroll & Branch IDs",
    icon: <AccountBalanceOutlined />,
    fields: [
      field("Agent ID / CL Payroll Number", agent?.agentId),
      field("CL Branch Number", agent?.clientlinqBranch),
      field("USA CL Payroll Number", agent?.usClientlinqId),
      field("USA CL Branch Number", agent?.usClientlinqBranch),
    ],
  },
  licenses: {
    id: "licenses",
    title: "Licenses & Insurance",
    icon: <GavelOutlined />,
    fields: [
      field("Master Business License", agent?.masterBusinessLicense),
      field("TICO License", agent?.licenses?.tico),
      field("CPBC Number", agent?.licenses?.bccp),
      field("OPC Number", agent?.licenses?.opc),
      field("Insurance License", agent?.insuranceLicense),
      field("Tour Ops Insurance", agent?.tourOpsInsurance),
      field("Manulife Branch Number", agent?.manulifeBranch),
      field("Tugo Branch Number", agent?.tugoBranch),
    ],
  },
  operations: {
    id: "operations",
    title: "Operations & Cards",
    icon: <CreditCardOutlined />,
    fields: [
      field("Bricks And Mortar", agent?.bricksAndMortar),
      field("Has GDS (Ex. Sabre)", agent?.hasSabre),
      field("IATA Card", agent?.iataCard),
      field("Corp CC", agent?.bank?.corpCC),
      field("Corp CC Card Type", agent?.bank?.corpCCType),
      field("Corp CC (Temporary)", agent?.corpCC),
    ],
  },
  marketingContact: {
    id: "marketingContact",
    title: "Marketing Contact",
    icon: <ContactMailOutlined />,
    fields: [
      field("Marketing First Name", agent?.marketing?.firstname),
      field("Marketing Last Name", agent?.marketing?.lastname),
      field("Marketing Email", agent?.marketing?.email, "email"),
      field("Custom Email", agent?.customEmailAddress, "email"),
      field("Marketing Phone", agent?.marketing?.phone, "phone"),
      field("Marketing Street Address", agent?.marketing?.streetAddress),
      field("Marketing City", agent?.marketing?.city),
      field("Marketing Province", agent?.marketing?.province),
      field("Marketing Postal Code", agent?.marketing?.postalCode),
    ],
  },
  marketingPrefs: {
    id: "marketingPrefs",
    title: "Marketing Preferences",
    icon: <CampaignOutlined />,
    fields: [
      field("Email Campaigns Allowed", agent?.marketing?.emailsAllowed),
      field("Direct Mail Pieces Allowed", agent?.marketing?.mailPiecesAllowed),
      field("On TTAND Facebook", agent?.onTTANDFacebook),
      field("FB Groups", agent?.fbGroups, "text", true),
    ],
  },
  web: {
    id: "web",
    title: "Brand & Website",
    icon: <LanguageOutlined />,
    fields: [
      field("Branded As", agent?.brandedAs),
      field("Website URL", agent?.websiteURL, "url"),
      field("B2C Website Completion", agent?.dates?.websiteCompletion),
      field("Air Hotel Cruise Dynamic Packages Token", agent?.websiteCode),
      field("Luxe Agent", agent?.isLuxeAgent),
      field("Luxe Certified Advisor", agent?.isLuxeCertifiedAdvisor),
    ],
  },
  privacy: {
    id: "privacy",
    title: "Privacy & Sharing",
    icon: <PrivacyTipOutlined />,
    fields: [
      field("Lead Program Authorized", agent?.lgpMember),
      field("Commission Sharing", formatTriState(agent?.commisionSharing)),
      field("Share - Agent to Agent", formatTriState(agent?.sharinga2a)),
      field("Share - Agent to Consumer", formatTriState(agent?.sharinga2c)),
      field("Private Agent", agent?.secretAgent),
    ],
  },
  bank: {
    id: "bank",
    title: "Bank Information",
    icon: <AccountBalanceOutlined />,
    fields: [
      field("Bank Name", agent?.bank?.name),
      field("Bank Code (3 Digits Institution No.)", agent?.bank?.code),
      field("Bank Transit No. (5 Digits)", agent?.bank?.transitNumber),
      field("Bank Account No. (7-12 Digits)", agent?.bank?.accountNumber, "secret"),
      field("Bank SIN", agent?.bank?.siNumber, "secret"),
      field("HST/GST #", agent?.bank?.st),
      field("QST #", agent?.bank?.qst),
      field("T4A Should be Issued in this Name", agent?.bank?.t4aName),
      field("Brand Name on RBC", agent?.bank?.brandNameOnRBC),
    ],
  },
  finance: {
    id: "finance",
    title: "Finance",
    icon: <TrendingUpOutlined />,
    fields: [field(`Rising Star ${RISING_STAR_YEAR}`, getRisingStarLevel(agent))],
  },
  expertise: {
    id: "expertise",
    title: "Availability & Skills",
    icon: <TuneOutlined />,
    fields: [
      field("Working Days", getWorkingDays(agent?.workingDays), "text", true),
      field("Languages Spoken", getLanguagesFullNames(agent?.languageExpertise), "text", true),
      field("Booking System Expertise", getBookingSystemsFullNames(agent?.bookingSystemExpertise), "text", true),
      field("Certifications", getCertificationNames(agent), "text", true),
    ],
  },
});

export type ProfileTabValue = "overview" | "contact" | "business" | "marketing" | "banking" | "expertise" | "notes";

export const PROFILE_TABS: { value: ProfileTabValue; label: string; groupIds?: string[] }[] = [
  { value: "overview", label: "Overview" },
  { value: "contact", label: "Contact", groupIds: ["emails", "phones", "address", "callPlans"] },
  {
    value: "business",
    label: "Business & Compliance",
    groupIds: ["account", "dates", "licenses", "payroll", "operations"],
  },
  { value: "marketing", label: "Marketing", groupIds: ["web", "marketingPrefs", "marketingContact", "privacy"] },
  { value: "banking", label: "Banking & Finance", groupIds: ["bank", "finance"] },
  { value: "expertise", label: "Expertise", groupIds: ["expertise"] },
  { value: "notes", label: "Notes" },
];

// older links use ?tab=profile-section / ?tab=notes-section
export const LEGACY_TAB_VALUES: Record<string, ProfileTabValue> = {
  "profile-section": "overview",
  "notes-section": "notes",
};

export const getTabForGroup = (groupId: string) => PROFILE_TABS.find((tab) => tab.groupIds?.includes(groupId));

const findField = (groups: Record<string, ProfileFieldGroup>, label: string) =>
  Object.values(groups)
    .flatMap((group) => group.fields)
    .find((f) => f.label === label);

// the handful of things staff look up most, shown on the overview
const KEY_DETAIL_LABELS = [
  "Agent Type",
  "Agreement Type",
  "TTAND Extension",
  "Cell Phone",
  "Business Email",
  "Agent ID / CL Payroll Number",
  "TICO License",
  "Branded As",
  "Website URL",
  "Address",
];

export const getKeyDetails = (groups: Record<string, ProfileFieldGroup>) =>
  KEY_DETAIL_LABELS.map((label) => findField(groups, label)).filter((f): f is ProfileField => !!f);

// milestones in the order they normally happen
const TIMELINE_LABELS = [
  "Start Date in Travel Industry",
  "Original TTAND Join Date",
  "Contract Commencement Date",
  "Training Date",
  "Business Card Completion Date",
  "B2C Website Completion",
  "Monthly Fees Payment Started",
  "Termination Date",
];

export const getTimeline = (groups: Record<string, ProfileFieldGroup>) =>
  TIMELINE_LABELS.map((label) => findField(groups, label)).filter((f): f is ProfileField => !!f?.value);

export const searchFieldGroups = (groups: Record<string, ProfileFieldGroup>, query: string) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  return Object.values(groups)
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (f) =>
          f.label.toLowerCase().includes(needle) || (f.kind !== "secret" && f.value?.toLowerCase().includes(needle)),
      ),
    }))
    .filter((group) => group.fields.length);
};

export const DESTINATION_FIELDS = [
  { label: "Canada", key: "canada" },
  { label: "USA", key: "usa" },
  { label: "Caribbean", key: "caribbean" },
  { label: "California", key: "california" },
  { label: "Central America", key: "centralAmerica" },
  { label: "Florida", key: "florida" },
  { label: "Las Vegas", key: "lasvegas" },
  { label: "Mexico", key: "mexico" },
  { label: "Asia", key: "asia" },
  { label: "Africa", key: "africa" },
  { label: "Australia", key: "australia" },
  { label: "Europe", key: "europe" },
  { label: "New Zealand", key: "newzealand" },
  { label: "South America", key: "southAmerica" },
] as const;

export const PRODUCT_FIELDS = [
  { label: "Air Only", key: "aironly" },
  { label: "Cruise", key: "cruise" },
  { label: "Eco Adventure", key: "ecoAdventure" },
  { label: "Luxury", key: "luxury" },
  { label: "Motor Coach", key: "motorCoach" },
  { label: "Sun & Beaches", key: "sunAndBeaches" },
  { label: "Tours", key: "tours" },
  { label: "Wedding", key: "wedding" },
  { label: "Groups", key: "groups" },
  { label: "Families", key: "families" },
  { label: "Couples", key: "couples" },
  { label: "LGBTQ+", key: "glbtq" },
  { label: "Singles", key: "singles" },
  { label: "Students", key: "students" },
] as const;

// same scale the agent picks from in their preferences (see EXPERTISE_LEVEL_OPTIONS)
export const EXPERTISE_LEVELS = ["Little", "I am familiar", "I Rock", "I am all Star"] as const;
